import type { EpochMs, Freshness, DataQuality } from './types.ts';
import { M_PER_FT, M_PER_NM, normalizeDeg } from './geo.ts';
import { zonedParts } from './tz.ts';

/** Placeholder for a missing value. */
export const NO_VALUE = '—';
/** U+2212 MINUS SIGN — same advance width as '+' in tabular fonts. */
export const MINUS = '−';

const groupFmt = new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 });

/** Integer with thousands separators and a U+2212 minus. */
export function formatInt(n: number): string {
  const r = Math.round(n);
  const s = groupFmt.format(Math.abs(r));
  return r < 0 ? MINUS + s : s;
}

const ok = (n: number | null | undefined): n is number => n != null && Number.isFinite(n);

/** "4.2 NM" (≥0.5 NM, one decimal below 100 NM), "820 ft" below (nearest 10 ft). */
export function formatDistance(m: number | null): string {
  if (!ok(m)) return NO_VALUE;
  const nm = Math.abs(m) / M_PER_NM;
  if (nm >= 0.5) {
    const txt = nm < 99.95 ? nm.toFixed(1) : formatInt(nm);
    return `${txt} NM`;
  }
  return `${formatInt(Math.round(Math.abs(m) / M_PER_FT / 10) * 10)} ft`;
}

/** "2,450 ft". */
export function formatAltitude(ft: number | null): string {
  if (!ok(ft)) return NO_VALUE;
  return `${formatInt(ft)} ft`;
}

/** "084°" */
export function formatBearing(deg: number | null): string {
  if (!ok(deg)) return NO_VALUE;
  const r = Math.round(normalizeDeg(deg)) % 360;
  return `${String(r).padStart(3, '0')}°`;
}

/** "112 kt" */
export function formatSpeed(kt: number | null): string {
  if (!ok(kt)) return NO_VALUE;
  return `${formatInt(kt)} kt`;
}

/** Below this |fpm| the aircraft reads as level (ADS-B VS resolution is 64 fpm). */
export const LEVEL_VS_FPM = 100;

/** "+850 fpm" / "−400 fpm" / "level". Rounded to 10 fpm. */
export function formatVs(fpm: number | null): string {
  if (!ok(fpm)) return NO_VALUE;
  if (Math.abs(fpm) < LEVEL_VS_FPM) return 'level';
  const r = Math.round(fpm / 10) * 10;
  return `${r > 0 ? '+' : MINUS}${groupFmt.format(Math.abs(r))} fpm`;
}

/** "3 s", "1 min 20 s", "2 h 05 min". Floors to whole seconds; negatives get a U+2212 prefix. */
export function formatDuration(sec: number | null): string {
  if (!ok(sec)) return NO_VALUE;
  const neg = sec < 0;
  const s = Math.floor(Math.abs(sec));
  let out: string;
  if (s < 60) out = `${s} s`;
  else if (s < 3600) out = `${Math.floor(s / 60)} min ${String(s % 60).padStart(2, '0')} s`;
  else out = `${Math.floor(s / 3600)} h ${String(Math.floor((s % 3600) / 60)).padStart(2, '0')} min`;
  return neg ? MINUS + out : out;
}

/** "12 s ago". Null → "no data"; negative ages (clock skew) read as "0 s ago". */
export function formatAge(sec: number | null): string {
  if (!ok(sec)) return 'no data';
  return `${formatDuration(Math.max(0, sec))} ago`;
}

const pad2 = (n: number) => String(n).padStart(2, '0');

/** "17:42:10Z" (default) or "17:42Z". */
export function formatUtc(t: EpochMs, withSeconds = true): string {
  const d = new Date(t);
  const hm = `${pad2(d.getUTCHours())}:${pad2(d.getUTCMinutes())}`;
  return withSeconds ? `${hm}:${pad2(d.getUTCSeconds())}Z` : `${hm}Z`;
}

const zoneNameCache = new Map<string, Intl.DateTimeFormat>();

/** Short zone abbreviation at `t`, e.g. "PDT"; falls back to "GMT+2" style where no abbreviation exists. */
export function zoneAbbreviation(t: EpochMs, timezone: string): string {
  let f = zoneNameCache.get(timezone);
  if (!f) {
    f = new Intl.DateTimeFormat('en-US', { timeZone: timezone, timeZoneName: 'short' });
    zoneNameCache.set(timezone, f);
  }
  const part = f.formatToParts(new Date(t)).find((p) => p.type === 'timeZoneName');
  return part?.value ?? timezone;
}

/** "10:42:10 PDT" (default) or "10:42 PDT" in the given IANA zone. */
export function formatLocal(t: EpochMs, timezone: string, withSeconds = true): string {
  const p = zonedParts(t, timezone);
  const hm = `${pad2(p.hour)}:${pad2(p.minute)}`;
  return `${withSeconds ? `${hm}:${pad2(p.second)}` : hm} ${zoneAbbreviation(t, timezone)}`;
}

const POINTS16 = ['N', 'NNE', 'NE', 'ENE', 'E', 'ESE', 'SE', 'SSE', 'S', 'SSW', 'SW', 'WSW', 'W', 'WNW', 'NW', 'NNW'] as const;

/** 16-point compass "WSW". */
export function compassPoint(deg: number): string {
  if (!Number.isFinite(deg)) return NO_VALUE;
  return POINTS16[Math.round(normalizeDeg(deg) / 22.5) % 16]!;
}

const FRESHNESS_LABELS: Record<Freshness, string> = {
  live: 'Live',
  aging: 'Aging',
  stale: 'Stale',
  lost: 'Lost',
  none: 'No data',
};

export function freshnessLabel(f: Freshness): string {
  return FRESHNESS_LABELS[f] ?? String(f);
}

const QUALITY_LABELS: Record<DataQuality, string> = {
  observed: 'Observed',
  interpolated: 'Interpolated',
  estimated: 'Estimated',
  predicted: 'Predicted',
  manual: 'Manual',
};

export function qualityLabel(q: DataQuality): string {
  return QUALITY_LABELS[q] ?? String(q);
}
